import { Box, Button, Typography, useTheme } from "@mui/material";
import {
  BoltRounded,
  CalculateOutlined,
  CampaignOutlined,
  ChatBubbleOutlineRounded,
  CheckRounded,
  EmojiEventsOutlined,
  FlagOutlined,
  ForumOutlined,
  HourglassTopOutlined,
  NewspaperOutlined,
  PaymentsOutlined,
  PeopleAltOutlined,
  PsychologyOutlined,
  ScheduleOutlined,
  SchoolOutlined,
  SearchRounded,
  SellOutlined,
  ShowChartRounded,
  TimerOutlined,
  TravelExploreRounded,
  TrendingUpRounded,
} from "@mui/icons-material";
import { motion, useReducedMotion } from "framer-motion";
import { OnboardingAnswers } from "../onboardingStorage";
import { AnswerKey, OnboardingStep, QUESTION_COUNT, questionIndexOf } from "../onboardingSteps";
import { onboardingSpring, staggerChild, staggerParent } from "../onboardingMotion";
import {
  GREEN,
  eyebrowSx,
  hairline,
  primaryButtonSx,
  stepSubtitleSx,
  stepTitleSx,
  textPrimary,
} from "./stepStyles";

export type QuestionStepData = Extract<OnboardingStep, { kind: "question" }>;

/** Option icons map 1:1 to the option's meaning or are omitted (markets'
    country options have no honest icon, so that question goes text-only). */
const OPTION_ICONS: Record<string, typeof CheckRounded> = {
  "experience:starting": FlagOutlined,
  "experience:casual": ScheduleOutlined,
  "experience:experienced": ShowChartRounded,
  "experience:trader": BoltRounded,
  "goal:wealth": TrendingUpRounded,
  "goal:income": PaymentsOutlined,
  "goal:outperform": EmojiEventsOutlined,
  "goal:learn": SchoolOutlined,
  "decide:social": ChatBubbleOutlineRounded,
  "decide:news": NewspaperOutlined,
  "decide:gut": PsychologyOutlined,
  "decide:research": SearchRounded,
  "decide:communities": ForumOutlined,
  "decide:friends": PeopleAltOutlined,
  "struggle:what_to_buy": TravelExploreRounded,
  "struggle:when_to_sell": SellOutlined,
  "struggle:financials": CalculateOutlined,
  "struggle:noise": CampaignOutlined,
  "time:low": TimerOutlined,
  "time:medium": ScheduleOutlined,
  "time:high": HourglassTopOutlined,
};

interface QuestionStepProps {
  step: QuestionStepData;
  stepIndex: number;
  answers: OnboardingAnswers;
  pendingAnswer: string | null;
  onAnswer: (answerKey: AnswerKey, key: string) => void;
  onToggleMulti: (answerKey: AnswerKey, key: string) => void;
  onSubmitMulti: (answerKey: AnswerKey) => void;
}

export const QuestionStep = ({
  step,
  stepIndex,
  answers,
  pendingAnswer,
  onAnswer,
  onToggleMulti,
  onSubmitMulti,
}: QuestionStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();

  const selectedCount = ((answers[step.answerKey] as string[] | undefined) ?? []).length;
  const questionNumber = questionIndexOf(stepIndex) + 1;

  return (
    <Box sx={{ pt: 1, display: "flex", flexDirection: "column", flex: 1 }}>
      <Typography sx={eyebrowSx(isDark)}>
        Question {questionNumber} of {QUESTION_COUNT}
      </Typography>
      <Typography sx={{ ...stepTitleSx(isDark), mt: 0.75 }}>{step.title}</Typography>
      {step.subtitle && <Typography sx={stepSubtitleSx(isDark)}>{step.subtitle}</Typography>}
      <Box
        component={motion.div}
        variants={reduced ? undefined : staggerParent}
        initial={reduced ? undefined : "hidden"}
        animate={reduced ? undefined : "visible"}
        sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 1.25 }}
      >
        {step.options.map((option) => {
          const selected = step.multi
            ? ((answers[step.answerKey] as string[] | undefined) ?? []).includes(option.key)
            : pendingAnswer === option.key ||
              (pendingAnswer === null && answers[step.answerKey] === option.key);
          const Icon = OPTION_ICONS[`${step.answerKey}:${option.key}`];
          return (
            <motion.div
              key={option.key}
              variants={reduced ? undefined : staggerChild}
              whileTap={reduced ? undefined : { scale: 0.97 }}
            >
              <Button
                fullWidth
                disableRipple={!step.multi}
                onClick={() =>
                  step.multi
                    ? onToggleMulti(step.answerKey, option.key)
                    : onAnswer(step.answerKey, option.key)
                }
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: textPrimary(isDark),
                  px: 2,
                  py: 2.25,
                  borderRadius: 3,
                  gap: 1.5,
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                  border: selected
                    ? `1.5px solid ${GREEN}`
                    : `1px solid ${hairline(isDark)}`,
                  // Glass over the aurora, matching the auth screens' fields.
                  backgroundColor: selected
                    ? "rgba(34, 197, 94, 0.12)"
                    : isDark
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(12px)",
                }}
              >
                {Icon && (
                  <Icon
                    sx={{
                      fontSize: 22,
                      color: selected ? GREEN : isDark ? "#9ca3af" : "#64748b",
                      transition: "color 0.15s ease",
                    }}
                  />
                )}
                <Box component="span" sx={{ flex: 1 }}>
                  {option.label}
                </Box>
                {selected && (
                  <motion.span
                    initial={reduced ? undefined : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={onboardingSpring}
                    style={{ display: "flex" }}
                  >
                    <CheckRounded sx={{ fontSize: 20, color: GREEN }} />
                  </motion.span>
                )}
              </Button>
            </motion.div>
          );
        })}
      </Box>
      {step.multi && (
        <>
          <Box sx={{ flex: 1 }} />
          <Button
            fullWidth
            disabled={selectedCount === 0}
            onClick={() => onSubmitMulti(step.answerKey)}
            sx={{ ...primaryButtonSx, mt: 3 }}
          >
            {selectedCount === 0 && "Continue"}
            {selectedCount > 0 && `Continue · ${selectedCount} selected`}
          </Button>
        </>
      )}
    </Box>
  );
};
